import { type ReactNode } from "react";
import type { ComplianceFramework } from "./use-compliance.ts";
import { COMPLIANCE_FRAMEWORKS, frameworkLabel, frameworkTitle } from "./vocab.ts";

/**
 * The framework picker (task 10.3; design C6; Requirement 4.1). It lets the operator pick ONE of the
 * four frameworks; selecting one re-queries, because the hook (`useComplianceMapping`) keys on the
 * chosen framework. The four come from the generated {@link ComplianceFramework} enum via
 * {@link COMPLIANCE_FRAMEWORKS} — the SPA holds no client-authored framework catalog (Requirement
 * 4.5); only the display LABELS are mapped here, and the references/titles come from the server.
 *
 * Accessibility: it is a real tablist (`role="tablist"`) of `role="tab"` buttons, each with
 * `aria-selected` reflecting the current selection, so the active framework is conveyed as state and
 * not by colour alone. The buttons are focusable controls, consistent with the repo's button-driven
 * controls.
 */

export interface FrameworkPickerProps {
  readonly selected: ComplianceFramework;
  readonly onSelect: (framework: ComplianceFramework) => void;
}

export function FrameworkPicker(props: FrameworkPickerProps): ReactNode {
  return (
    <div
      className="cmp-picker"
      role="tablist"
      aria-label="Compliance framework"
      aria-orientation="horizontal"
    >
      {COMPLIANCE_FRAMEWORKS.map((framework) => {
        const isSelected = framework === props.selected;
        return (
          <button
            key={framework}
            type="button"
            role="tab"
            id={`cmp-framework-tab-${framework}`}
            aria-selected={isSelected}
            aria-controls="cmp-framework-panel"
            className="cmp-picker__tab"
            data-selected={isSelected ? "true" : undefined}
            title={frameworkTitle(framework)}
            onClick={() => {
              if (!isSelected) props.onSelect(framework);
            }}
          >
            {frameworkLabel(framework)}
          </button>
        );
      })}
    </div>
  );
}
